import secureAxios from "./axios/secureAxios";
import { uploadImage } from "./imageUpload";

/**
 * Upload a file for a model media parameter and return a normalized result.
 *
 * Each backend route answers with a different key -- `/image-upload` returns
 * `image_url`, `/video-upload` returns `video_url`, `/audio-upload` returns
 * `audio_url`. Call sites used to guess with `res?.image_url || res?.url`
 * (ZenControlPanel), where `url` was never returned by anything. Normalizing
 * here means callers read `.url` and stop guessing.
 *
 * Video and audio stream straight to S3, so they are NOT compressed and must
 * never be routed through /image-upload (20 MB cap, buffers + base64-encodes
 * the whole file).
 *
 * @param {File|Blob} file
 * @param {'image'|'video'|'audio'} kind
 * @returns {Promise<{ url: string }>}
 */
export async function uploadMediaFile(file, kind) {
  try {
    if (kind === 'image') {
      const data = await uploadImage(file);
      const url = data?.image_url || data?.data?.image_url;
      if (!url) throw new Error('Image upload returned no URL');
      return { url };
    }

    const route = kind === 'video' ? '/video-upload/upload' : '/audio-upload/upload';
    const formData = new FormData();
    formData.append(kind, file); // field name must match the route: 'video' / 'audio'

    const { data } = await secureAxios.post(route, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    const url = kind === 'video' ? data?.video_url : data?.audio_url;
    if (!url) throw new Error(data?.error || `${kind} upload returned no URL`);
    return { url };
  } catch (err) {
    throw new Error(uploadErrorMessage(err));
  }
}

/**
 * uro-api error bodies are OBJECTS ({ success:false, error:'…' }) and
 * secureAxios rejects with `error.response?.data || error` — so in a uro-web
 * catch there is NO `err.response` and NO `err.message`, only `err.error`.
 * Normalizing here means callers can just read `err.message` and the real
 * server text ("File too large — maximum size is 25 MB", "Unsupported file
 * type: …") actually reaches the user.
 */
function uploadErrorMessage(err) {
  return err?.error                 // uro-api body, unwrapped by secureAxios
    || err?.response?.data?.error   // in case a caller passes a raw axios error
    || err?.message                 // a real Error (incl. ones thrown above)
    || 'Upload failed';
}

const mediaUploadActions = {
  uploadMediaFile,
};

export default mediaUploadActions;
